import { useCallback, useEffect, useState } from 'react';
import { getUploadSignedUrl } from '@/services/uploads.service';

/** Re-sign this long before the server's expiry, so a shown photo never 403s. */
const RESIGN_MARGIN_MS = 30_000;

interface SignedUrlState {
  url: string | null;
  mimeType: string | null;
  loading: boolean;
  error: boolean;
}

const IDLE: SignedUrlState = {
  url: null,
  mimeType: null,
  loading: false,
  error: false,
};

/**
 * The signed URL of a photo or file the parent added (YAR-556,
 * `uploads-signed-url`, 1 h TTL). Fetches while `enabled`, re-signs before
 * the URL expires (keeping the current one if that fails), and `retry()`
 * drops the URL and signs a fresh one.
 */
export function useUploadSignedUrl(
  emailId: string | null,
  enabled: boolean,
): SignedUrlState & { retry(): void } {
  const [state, setState] = useState<SignedUrlState>(IDLE);
  // Bumped by retry() and by the expiry timer; each bump is a fresh fetch.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!emailId || !enabled) {
      setState(IDLE);
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Keep a shown photo on screen while it is re-signed.
    setState((prev) => ({ ...prev, loading: prev.url === null, error: false }));
    getUploadSignedUrl(emailId).then(
      ({ url, mimeType, expiresIn }) => {
        if (cancelled) return;
        setState({ url, mimeType, loading: false, error: false });
        const resignIn = Math.max(0, expiresIn * 1000 - RESIGN_MARGIN_MS);
        timer = setTimeout(() => setAttempt((n) => n + 1), resignIn);
      },
      () => {
        if (cancelled) return;
        // A failed renewal keeps a photo that still works; the view turns an
        // actual load failure into the error line.
        setState((prev) =>
          prev.url
            ? { ...prev, loading: false, error: false }
            : { url: null, mimeType: null, loading: false, error: true },
        );
      },
    );
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [emailId, enabled, attempt]);

  // A retry drops the URL it had: it failed, so never show it again.
  const retry = useCallback(() => {
    setState({ ...IDLE, loading: true });
    setAttempt((n) => n + 1);
  }, []);
  return { ...state, retry };
}
